import { Location } from "@angular/common";
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Navigate } from "@ngxs/router-plugin";
import { Actions, ofActionCompleted, Select, Store } from "@ngxs/store";
import { Observable } from "rxjs";
import { Subject } from "rxjs";
import { take } from "rxjs/operators";
import { takeUntil } from "rxjs/operators";

import { SaveLocalQuestion } from "../../../permit-create/states/permit-question-storage.state";
import { PermitQuestionActions } from "../../../permit-create/states/permit-question.actions";
import { PermitPaths } from "../../../permit-paths";
import { PermitStepActions } from "../../../permit/states/permit-step.action";
import { PreparedPermitListItem } from "../../models/prepared-permit-list-item.model";
import { PermitListActions } from "../../states/permit-list.actions";
import { PermitListSelectors } from "../../states/permit-list.selectors";

@Component({
    selector: "pmt-prepared-permit-list",
    templateUrl: "./prepared-permit-list.component.html",
    styleUrls: ["./prepared-permit-list.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreparedPermitListComponent implements OnInit, OnDestroy {
    @Select(PermitListSelectors.preparedPermits)
    public preparedPermits$!: Observable<PreparedPermitListItem[]>;

    @Select(PermitListSelectors.retrievableFlammableAtexPermits)
    public retrievableFlammableAtexPermits$!: Observable<PreparedPermitListItem[]>;

    @Select(PermitListSelectors.loading)
    public loading$!: Observable<boolean>;

    @Select(PermitListSelectors.retrievableFlammableAtexPermitErrorMessage)
    public errorMessage$!: Observable<string | undefined>;

    public permits$!: Observable<PreparedPermitListItem[]>;
    public isRetrieveFlammableAtexPermit = false;
    public permitId = 0;

    private continueFlow = false;
    private destroy$ = new Subject<void>();

    constructor(
        private store: Store,
        private location: Location,
        private route: ActivatedRoute,
        private actions$: Actions,
    ) {}

    public ngOnInit(): void {
        this.isRetrieveFlammableAtexPermit = this.route.snapshot.data.retrieveFlammableAtexPermit === true;
        this.permits$ = this.isRetrieveFlammableAtexPermit ? this.retrievableFlammableAtexPermits$ : this.preparedPermits$;

        if (this.isRetrieveFlammableAtexPermit) {
            const permitIdParam = this.route.snapshot.paramMap.get("permitId") ?? "";
            this.permitId = parseInt(permitIdParam, 10);
            this.continueFlow = this.route.snapshot.queryParamMap.get("continueFlow") === "true";

            if (isNaN(this.permitId) || this.permitId <= 0) {
                this.store.dispatch(new Navigate([PermitPaths.Root]));
                return;
            }

            this.actions$.pipe(
                ofActionCompleted(PermitListActions.ConnectStandaloneFlammableAtexPermit),
                takeUntil(this.destroy$),
            ).subscribe(() => {
                const connected = this.store.selectSnapshot(PermitListSelectors.retrievableFlammableAtexPermitConnected) === true;
                if (!connected) {
                    return;
                }

                if (this.continueFlow) {
                    this.actions$.pipe(
                        ofActionCompleted(PermitStepActions.GoForward),
                        take(1),
                    ).subscribe(() => {
                        this.store.dispatch(new Navigate([PermitPaths.Root, this.permitId.toString()]));
                    });

                    this.store.dispatch([
                        new PermitQuestionActions.ResetToDefault(),
                        new SaveLocalQuestion(this.permitId, undefined),
                        new PermitStepActions.GoForward(this.permitId),
                    ]);
                    return;
                }

                this.store.dispatch(new Navigate([PermitPaths.Root, this.permitId.toString()]));
            });
        }

        this.refresh();
    }

    public refresh(): void {
        if (this.isRetrieveFlammableAtexPermit) {
            this.store.dispatch(new PermitListActions.GetRetrievableFlammableAtexPermits(this.permitId));
            return;
        }

        this.store.dispatch(new PermitListActions.GetAllPrepared());
    }

    public cancel(): void {
        if (this.isRetrieveFlammableAtexPermit) {
            this.store.dispatch(new Navigate([PermitPaths.Root, this.permitId.toString()]));
            return;
        }

        this.location.back();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
